import type { HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { cn } from '@lean-agility/design-system/react';

const dependencyBadgeVariants = cva(`inline-flex items-center justify-center rounded-md`, {
  variants: {
    direction: {
      incoming: '', // Arrow pointing left (this is blocked BY something)
      outgoing: '', // Arrow pointing right (this BLOCKS something)
    },
    status: {
      default: 'bg-bg-muted text-text-secondary',
      resolved: `
        bg-feedback-success/10 text-feedback-success
        dark:bg-feedback-success/20
      `,
      unresolved: `
        bg-feedback-warning/10 text-feedback-warning
        dark:bg-feedback-warning/20
      `,
    },
    size: {
      sm: 'size-5',
      md: 'size-6',
    },
  },
  defaultVariants: {
    direction: 'incoming',
    status: 'default',
    size: 'md',
  },
});

const iconSizeClasses = {
  sm: 'h-3 w-3',
  md: 'h-4 w-4',
};

export interface DependencyBadgeProps
  extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'>, VariantProps<typeof dependencyBadgeVariants> {
  /**
   * Direction of the dependency.
   * - `incoming`: This deliverable is blocked BY the dependency (← arrow)
   * - `outgoing`: This deliverable BLOCKS the dependency (→ arrow)
   */
  direction: 'incoming' | 'outgoing';
}

/**
 * DependencyBadge - A visual indicator showing dependency direction and status.
 *
 * Used to indicate whether a deliverable is blocked by (incoming) or
 * blocks (outgoing) another deliverable.
 *
 * @example
 * ```tsx
 * // Incoming dependency (this deliverable is blocked by something)
 * <DependencyBadge direction="incoming" status="unresolved" />
 *
 * // Outgoing dependency (this deliverable blocks something)
 * <DependencyBadge direction="outgoing" status="resolved" />
 * ```
 */
export function DependencyBadge({ direction, status, size, className, ...props }: DependencyBadgeProps) {
  const Icon = direction === 'incoming' ? ArrowLeft : ArrowRight;
  const iconSize = iconSizeClasses[size ?? 'md'];
  const label = direction === 'incoming' ? 'Blocked by' : 'Blocks';

  return (
    <span className={cn(dependencyBadgeVariants({ direction, status, size }), className)} aria-label={label} {...props}>
      <Icon className={iconSize} aria-hidden="true" />
    </span>
  );
}
